import i18next from "i18next";
import debounce from "lodash.debounce";
import React, { useMemo, useState } from "react";
import { useCurrentUserLogin } from "../hooks/useCurrentUserLogin";
import { toast } from "react-toastify";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useGetCurrentUserTags, useUpdateControl } from "../api/hooks/useRiskThemes";
import { useAdmin } from "../context/adminContext";
import { useDesignSystem } from "../context/designSystemContext";
import { Categories } from "../types/categories";
import { ControlType } from "../types/controlsRisk";
import { canEditRisk } from "../utils/canEditRisk";
import { showErrorToast } from "../utils/errorToast";
import { pSBC } from "../utils/pSBC";
import {
  transformDate2Front,
  transformValue2Front,
} from "../utils/transformValue";
import { EditControlButton } from "./editControlsButton";
import { NewControlButton } from "./newControlButton";
import { ViewHistory } from "./viewHistoryControlButton";


type RiskControlsProps = {
  riskTheme: Categories;
  controls: ControlType[];
  refetchRiskTheme: () => void;
};

export const RiskControls: React.FC<RiskControlsProps> = ({
  riskTheme,
  controls,
  refetchRiskTheme,
}) => {
  const isAdmin = Boolean(useAdmin());
  const { mutate: updateControl } = useUpdateControl();
  const { data: currentUserTags } = useGetCurrentUserTags();
  const [pendingId, setPendingId] = useState<number | null>(null);

  const { designSystem } = useDesignSystem();
  const currentUserLogin = useCurrentUserLogin();
  const canCurrentUserEditRisk = canEditRisk({
    isAdmin,
    riskTheme,
    currentUserLogin,
    currentUserTags,
  });

  /* ---------------------- definindo cor pela data -------------------------- */
  const defineColor = (control: any) => {
    const termDate = new Date(control.plannedFinishDate);
    const todayDate = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    );
    let finalDate: Date | null = null;

    if (control.actualFinishDate) {
      finalDate = new Date(control.actualFinishDate);
    }
    if (todayDate > termDate && !control.actualFinishDate) {
      return "bg-[#FF9999]";
    } else if (finalDate != null && termDate < finalDate) {
      return "bg-[#FF9999]";
    } else {
      return "bg-[#99FFCE]";
    }
  };

  /*---------------------------------------------------------------- */
  const handleGlobalGoalChange = (control: ControlType) => {
    setPendingId(control.id);
    debouncedUpdateControl(control);
  };

  const debouncedUpdateControl = useMemo(
    () =>
      debounce((control: ControlType) => {
        const updatedControl: ControlType = {
          ...control,
          globalGoal: !control.globalGoal,
        };

        updateControl(updatedControl, {
          onSuccess: () => {
            setPendingId(null);
            refetchRiskTheme();
            toast.success("Controle alterado com sucesso");
          },
          onError: (error) => {
            setPendingId(null);
            showErrorToast(error, "Erro: Controle não salvo");
          },
        });
      }, 300),
    [updateControl, refetchRiskTheme]
  );

  const sortedControls = useMemo(() => {
    return [...controls].sort((a, b) => {
      if (a.progressPerc !== 100 && b.progressPerc === 100) return -1;
      if (a.progressPerc === 100 && b.progressPerc !== 100) return 1;
      return a.id - b.id;
    });
  }, [controls]);

  const controlItemsCss =
    "pl-[4px] border rounded-md mr-2 bg-[#F6F4F4] font-openSans text-[13.06px] leading-[24.11px]";

  function shouldDisableOkrCheckbox(control: ControlType) {
    return !canCurrentUserEditRisk || pendingId === control.id
  }

  return (
    <div className='static pl-5 pt-4 m-4 border rounded-lg shadow-lg p-2 bg-white'>
      <div className='font-openSans font-bold mb-4' style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>
        {i18next.t("controls")}
      </div>
      <table className='w-full border-separate' border={1}>
        <tr className='font-openSans font-bold text-[13px] leading-[16px] text-[#414141]'>
          <td>{i18next.t("control")}</td>
          <td>{i18next.t("control_initial_date")}</td>
          <td>{i18next.t("control_actual_initial_date")}</td>
          <td>{i18next.t("control_term")}</td>
          <td>{i18next.t("control_final_date")}</td>
          <td>{i18next.t("control_going")}</td>
          <td>{"OKR"}</td>
          <td>{i18next.t("control_status")}</td>
          <td></td>
          <td>{i18next.t("control_progress")}</td>
        </tr>
        {sortedControls ? (
          sortedControls.map((control) => {
            return (
              <tr className='font-openSans pb-[10px]' key={control.id}>
                <td className='w-[24.3%]'>
                  <div
                    className={`${controlItemsCss} flex flex-row flex-between`}
                  >
                    <div
                      className={`w-[94%] line-clamp-1 hover:max-w-none`}
                      data-tooltip-content={control.name}
                      data-tooltip-id={`tooltip-${control.id}`}
                    >
                      {control.name}
                    </div>
                    <EditControlButton
                      control={control}
                      riskTheme={riskTheme}
                      refetchRiskTheme={refetchRiskTheme}
                    />
                  </div>
                  <ReactTooltip
                    id={`tooltip-${control.id}`}
                    place='top'
                    style={{ whiteSpace: "pre-wrap", maxWidth: "280px" }}
                  />
                </td>

                <td className='w-[6.5%]'>
                  <div className={controlItemsCss}>
                    {transformDate2Front(control.plannedStartDate)}
                  </div>
                </td>

                <td className='w-[6.5%]'>
                  <div className={controlItemsCss}>
                    {control.actualStartDate
                      ? transformDate2Front(control.actualStartDate)
                      : "-- / -- / ----"}
                  </div>
                </td>

                <td className='w-[6.5%]'>
                  <div className={controlItemsCss}>
                    {transformDate2Front(control.plannedFinishDate)}
                  </div>
                </td>

                <td className='w-[6.5%]'>
                  <div className={controlItemsCss}>
                    {control.actualFinishDate
                      ? transformDate2Front(control.actualFinishDate)
                      : "-- / -- / ----"}
                  </div>
                </td>

                <td className='w-[5.5%]'>
                  <div className={controlItemsCss}>{control.progressPerc}%</div>
                </td>

                <td className='w-[3.5%]'>
                  {" "}
                  {/* ORK */}
                  <div>
                    <input
                      className='flex items-center justify-center h-full ml-3'
                      type='checkbox'
                      checked={control.globalGoal}
                      onChange={() => handleGlobalGoalChange(control)}
                      disabled={shouldDisableOkrCheckbox(control)}
                    />
                  </div>
                </td>

                <td className='w-[8.72%] '>
                  <div
                    className={`bg-[#8D8D8D] pl-[4px] border rounded-md mr-2 font-openSans text-[13.06px] text-nowrap leading-[24.11px] text-white`}
                  >
                    {transformValue2Front(control.status, "status")}
                  </div>
                </td>

                <td className='w-[0.1%] '>
                  <div
                    className={`mr-1 flex items-center ${defineColor(
                      control
                    )} justify-center mr-2 w-[15px] h-[15px] rounded-full`}
                  ></div>
                </td>

                {/* PROGRESS */}
                <td className='w-[23.5%]'>
                  <div className={`${controlItemsCss} flex`}>
                    <div
                      className='w-[95%] line-clamp-1 hover:max-w-none'
                      data-tooltip-content={
                        control.currentEvolution?.description
                      }
                      data-tooltip-id={`tooltip-progress-${control.id}`}
                    >
                      {control.currentEvolution?.description}
                    </div>
                    <ViewHistory
                      canEditRisk={canCurrentUserEditRisk}
                      control={control}
                      refetchRiskTheme={refetchRiskTheme}
                    />
                  </div>
                  <ReactTooltip
                    id={`tooltip-progress-${control.id}`}
                    place='top'
                    style={{ whiteSpace: "pre-wrap", maxWidth: "280px" }}
                  />
                </td>
              </tr>
            );
          })
        ) : (
          <tr className='font-bold text-center'></tr>
        )}
      </table>
      {canCurrentUserEditRisk && <div className='flex justify-end'>
        <NewControlButton refetchRiskTheme={refetchRiskTheme} />
      </div>}
    </div>
  );
};
